import { BaseEmployeeDTORolesEnum, EmployeeDTOStatusEnum, SwitchableTenantHierarchyDTO, UserDTO, UserDTORolesEnum } from '../../api';
import { flattenTenants } from '../../utils/tenant';
import { apis } from '../api';
import { NamedTenant } from './hooks';

export function isGlobalAdmin(user: UserDTO | undefined): boolean {
  return !!user && (user.roles.includes(UserDTORolesEnum.Admin) || user.roles.includes(UserDTORolesEnum.SalesManager));
}

export interface AdminTenantAccessData {
  /** The tree of switchable tenants: its roots are the tenants the user reaches directly. */
  switchableTenants: SwitchableTenantHierarchyDTO[];
  canSwitch: boolean;
}

/**
 * The flat list of every tenant the user may switch to. The forest nests everything reachable below the tenant
 * it is reached through, so flattening it is the whole derivation.
 */
export function flattenSwitchableTenants(switchableTenants: SwitchableTenantHierarchyDTO[]): NamedTenant[] {
  return switchableTenants.flatMap(flattenTenants).map(node => ({ id: node.tenant.id, name: node.tenant.name }));
}

export function mayReadAllTenants(user: UserDTO | undefined): boolean {
  return (
    !!user &&
    (user.roles.includes(UserDTORolesEnum.Admin) ||
      user.roles.includes(UserDTORolesEnum.SalesManager) ||
      user.roles.includes(UserDTORolesEnum.SupportViewer))
  );
}

/**
 * Whether the user has anything that could make a tenant switchable at all. Deliberately generous - any global
 * role, any tenant role or any admin-level employment asks the API - so this only skips the request for ordinary
 * app users, for whom the endpoint could not return anything either.
 */
function couldSwitchTenants(user: UserDTO): boolean {
  return (
    mayReadAllTenants(user) ||
    (user.tenantRoles ?? []).length > 0 ||
    (user.employments ?? []).some(
      employment =>
        employment.status === EmployeeDTOStatusEnum.Active &&
        (employment.roles.includes(BaseEmployeeDTORolesEnum.TenantAdmin) ||
          employment.roles.includes(BaseEmployeeDTORolesEnum.TenantAssistant)),
    )
  );
}

export async function getAdminTenantAccessData(user: UserDTO | undefined): Promise<AdminTenantAccessData> {
  if (!user || !couldSwitchTenants(user)) {
    return { switchableTenants: [], canSwitch: false };
  }

  const { switchableTenants, canSwitch } = (await apis.tenants.getTenantHierarchies()).data;

  return { switchableTenants, canSwitch };
}
